/**
 * Filter Visibility card — CR-4802. Platform tab, beside Hierarchy Labels.
 *
 * Declares which filter controls this tenant's users see across the platform.
 * Each row is one filter: a switch for the declared value, a "detected" badge
 * while the tenant has not declared anything (the platform then reads its own
 * data — a division layer in the group tree, warehouse rows, and so on), and a
 * reset back to detection. Commodity is detected only, so it is listed for
 * information without a switch.
 *
 * Saves straight to the tenant's settings and cascades to the whole group; a
 * platform admin switched into a tenant edits that tenant, never the global row.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Switch } from '../../ui/switch';
import { SlidersHorizontal, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { coreAPI } from '../../../lib/api';
import type { FilterVisibilityPayload, FilterVisibilitySignals, FilterVisibilityOverrides } from '../../../lib/api';
import { useFilterVisibilityContext } from '../../../contexts/FilterVisibilityContext';

type Signal = keyof FilterVisibilitySignals;

const ROWS: Array<{ signal: Signal; label: string; help: string; declarable: boolean }> = [
  { signal: 'division', label: 'Division', help: 'Detected when the group has a division layer between it and its sites.', declarable: true },
  { signal: 'warehouse', label: 'Warehouse', help: 'Detected when warehouse data has been pulled in for any site.', declarable: true },
  { signal: 'supplier_code', label: 'Supplier Code', help: 'Detected when supplier codes are held against your suppliers.', declarable: true },
  { signal: 'supplier_type', label: 'Supplier Type', help: 'Internal / external supply. Shown unless switched off here.', declarable: true },
  { signal: 'historical_live', label: 'Data (Historical / Live)', help: 'Detected when both purchase order and delivery feeds are active.', declarable: true },
  { signal: 'company_country', label: 'Company Country', help: 'Detected when your sites span more than one country.', declarable: true },
  { signal: 'commodity', label: 'Commodity Group / Type', help: 'Shown when commodity groups are configured. Detected only.', declarable: false },
];

export function FilterVisibilityCard() {
  const { refresh } = useFilterVisibilityContext();
  const [payload, setPayload] = useState<FilterVisibilityPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Signal | null>(null);

  useEffect(() => {
    let cancelled = false;
    coreAPI.getFilterVisibility()
      .then((res) => { if (!cancelled) setPayload(res.data); })
      .catch(() => { if (!cancelled) setPayload(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const save = async (signal: Signal, value: boolean | null) => {
    setSaving(signal);
    try {
      const res = await coreAPI.updateFilterVisibility({ [signal]: value } as FilterVisibilityOverrides);
      setPayload(res.data);
      await refresh();
      toast.success(value === null ? 'Filter back on detection' : 'Filter visibility saved');
    } catch (err: unknown) {
      console.error('[FilterVisibilityCard] save failed:', err);
      toast.error('Could not save filter visibility');
    } finally {
      setSaving(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <SlidersHorizontal className="h-5 w-5" />
          Filter Visibility
        </CardTitle>
        <CardDescription>
          Which filters your users see across the platform. Each one is detected from your setup
          until you switch it on or off here; the setting applies to every site in your group.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading filter visibility…
          </div>
        ) : !payload ? (
          <p className="text-sm text-muted-foreground">Filter visibility is unavailable right now.</p>
        ) : !payload.editable ? (
          <p className="text-sm text-muted-foreground">Switch into a customer to configure its filters.</p>
        ) : (
          <div className="divide-y">
            {ROWS.map(({ signal, label, help, declarable }) => {
              const declared = payload.overrides[signal];
              const effective = payload.flags[signal];
              const detected = payload.detected[signal];
              const isDetected = declared === undefined;
              return (
                <div key={signal} className="flex items-center justify-between gap-4 py-3" data-testid={`fv-row-${signal}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{label}</span>
                      {isDetected ? (
                        <Badge variant="outline" className="text-xs">Detected · {detected ? 'shown' : 'hidden'}</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Set · {effective ? 'shown' : 'hidden'}</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{help}</p>
                  </div>
                  {declarable ? (
                    <div className="flex items-center gap-3 shrink-0">
                      {!isDetected && (
                        <Button variant="ghost" size="sm" onClick={() => save(signal, null)} disabled={saving !== null}>
                          Reset to detected
                        </Button>
                      )}
                      <Switch
                        checked={effective}
                        onCheckedChange={(checked) => save(signal, checked)}
                        disabled={saving !== null}
                        aria-label={`Show ${label} filter`}
                      />
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground shrink-0">{effective ? 'Shown' : 'Hidden'}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
